import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AdminModalTailwind from "./AdminModalTailwind";
import CategoryFormTailwind from "./CategoryFormTailwind";
import ConfirmationModal from "./ConfirmationModal";
import { categoriesService, ageGroupsService, pdfExportService } from "../../services";
import { getAgeGroupColors, getAgeGroupBadgeClasses, sortAgeGroups } from "../../utils/ageGroupColors";

const CategoriesTailwind = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedAgeGroup, setSelectedAgeGroup] = useState("all");
  const [categories, setCategories] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal und Form States
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const categoryFormRef = useRef();

  // Confirmation Modal States
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Toast Handler
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });

  // Export/Import States
  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  // Lade Daten beim ersten Laden
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Lade Kategorien und Altersgruppen parallel
      const [categoriesResponse, ageGroupsResponse] = await Promise.all([
        categoriesService.getAllCategories(),
        ageGroupsService.getAllAgeGroups()
      ]);

      if (categoriesResponse.success) {
        setCategories(categoriesResponse.data);
      } else {
        setError(categoriesResponse.error);
      }

      if (ageGroupsResponse.success) {
        setAgeGroups(ageGroupsResponse.data);
      } else {
        setError(ageGroupsResponse.error);
      }
    } catch (err) {
      setError("Fehler beim Laden der Daten");
      console.error("Fehler beim Laden der Daten:", err);
    } finally {
      setLoading(false);
    }
  };

  // Event Handlers
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
  };

  const handleAgeGroupChange = (ageGroup) => {
    setSelectedAgeGroup(ageGroup);
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setShowModal(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
  };

  const handleSaveCategory = async (categoryData) => {
    setIsSaving(true);
    try {
      if (editingCategory) {
        // Update existing category
        const response = await categoriesService.updateCategory(editingCategory.id, categoryData);
        if (response.success) {
          addToast('success', 'Kategorie erfolgreich aktualisiert');
          loadData();
          handleCloseModal();
        } else {
          addToast('error', response.error || 'Fehler beim Aktualisieren der Kategorie');
        }
      } else {
        // Create new category
        const response = await categoriesService.createCategory(categoryData);
        if (response.success) {
          addToast('success', 'Kategorie erfolgreich erstellt');
          loadData();
          handleCloseModal();
        } else {
          addToast('error', response.error || 'Fehler beim Erstellen der Kategorie');
        }
      }
    } catch (err) {
      addToast('error', 'Fehler beim Speichern der Kategorie');
      console.error("Fehler beim Speichern:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCategory = (category) => {
    setCategoryToDelete(category);
    setShowConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;

    try {
      const response = await categoriesService.deleteCategory(categoryToDelete.id);
      if (response.success) {
        addToast('success', 'Kategorie erfolgreich gelöscht');
        loadData();
      } else {
        addToast('error', response.error || 'Fehler beim Löschen der Kategorie');
      }
    } catch (err) {
      addToast('error', 'Fehler beim Löschen der Kategorie');
      console.error("Fehler beim Löschen:", err);
    } finally {
      handleCloseConfirmation();
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    setCategoryToDelete(null);
  };

  // Toast Handler
  const addToast = (type, message) => {
    setToast({ show: true, message, type });
    
    // Toast nach 5 Sekunden automatisch entfernen
    setTimeout(() => {
      setToast({ show: false, message: "", type: "info" });
    }, 5000);
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "info" });
  };

  // Export Handler
  const handleExportPDF = async () => {
    setIsExporting(true);
    setShowExportMenu(false);
    try {
      const filters = {
        type: selectedType,
        ageGroup: selectedAgeGroup,
        searchTerm: searchTerm
      };

      const result = await pdfExportService.exportCategoriesPDF(filters);
      
      if (result.success) {
        addToast('success', 'PDF erfolgreich heruntergeladen');
      } else {
        addToast('error', result.error || 'Fehler beim PDF-Export');
      }
    } catch (error) {
      addToast('error', 'Fehler beim PDF-Export');
      console.error("Fehler beim PDF-Export:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportJSON = async () => {
    setIsExporting(true);
    setShowExportMenu(false);
    try {
      const filters = {
        type: selectedType,
        ageGroup: selectedAgeGroup,
        searchTerm: searchTerm
      };

      const result = await pdfExportService.exportCategoriesJSON(filters);
      
      if (result.success) {
        addToast('success', 'JSON erfolgreich heruntergeladen');
      } else {
        addToast('error', result.error || 'Fehler beim JSON-Export');
      }
    } catch (error) {
      addToast('error', 'Fehler beim JSON-Export');
      console.error("Fehler beim JSON-Export:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // Import Handler
  const handleImportJSON = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      addToast('error', 'Bitte wählen Sie eine JSON-Datei aus');
      return;
    }

    setIsImporting(true);
    try {
      const fileContent = await file.text();
      const jsonData = JSON.parse(fileContent);

      // Unterstütze beide Formate: direktes Array oder { data: [...] } oder { categories: [...] }
      let categoriesArray = [];
      if (Array.isArray(jsonData)) {
        categoriesArray = jsonData;
      } else if (jsonData.data && Array.isArray(jsonData.data)) {
        categoriesArray = jsonData.data;
      } else if (jsonData.categories && Array.isArray(jsonData.categories)) {
        categoriesArray = jsonData.categories;
      } else {
        throw new Error('Ungültiges JSON-Format. Erwartet wird ein Array oder ein Objekt mit "data" oder "categories"');
      }

      const result = await pdfExportService.importCategoriesJSON(categoriesArray);
      
      if (result.success) {
        let message = result.message || 'JSON erfolgreich importiert';
        if (result.data) {
          message = `${message} (${result.data.created || 0} erstellt, ${result.data.updated || 0} aktualisiert${result.data.errors > 0 ? `, ${result.data.errors} Fehler` : ''})`;
        }
        addToast('success', message);
        // Daten neu laden
        await loadData();
      } else {
        addToast('error', result.error || 'Fehler beim JSON-Import');
      }
    } catch (error) {
      addToast('error', 'Fehler beim Importieren der JSON-Datei: ' + error.message);
      console.error("Fehler beim JSON-Import:", error);
    } finally {
      setIsImporting(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Schließe Export-Menü beim Klicken außerhalb
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.relative')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportMenu]);

  // Funktion für Altersgruppen-Farben (verwendet jetzt die Utility)
  const getAgeGroupColor = (ageGroup) => {
    return getAgeGroupColors(ageGroup).name;
  };

  const typeOptions = [
    { value: "all", label: t("admin.categories.allTypes", "Alle Typen") },
    { value: "Handform", label: t("admin.categories.handform", "Handform") },
    { value: "Waffenform", label: t("admin.categories.waffenform", "Waffenform") },
    { value: "Offene Form", label: t("admin.categories.offeneform", "Offene Form") },
    {value:"Partnerform", label: t("admin.categories.partnerform", "Partnerform")}
  ];

  const ageGroupOptions = [
    { value: "all", label: t("admin.categories.allAgeGroups", "Alle Altersgruppen") },
    ...sortAgeGroups(ageGroups).map((ageGroup) => ({
      value: ageGroup.name,
      label: ageGroup.name
    }))
  ];

  const filteredCategories = categories.filter((item) => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === "all" || item.type === selectedType;
    const matchesAgeGroup = selectedAgeGroup === "all" || item.ageGroups.includes(selectedAgeGroup);
    return matchesSearch && matchesType && matchesAgeGroup;
  });

  const handleViewGroups = (category) => {
    navigate(`/admin/groups?category=${encodeURIComponent(category.name)}`);
  };

  if (loading) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded w-64 mb-2"></div>
            <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-96"></div>
          </div>

          {/* Controls Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6 animate-pulse">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 h-[46px] bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
              <div className="flex gap-2">
                <div className="w-24 h-[46px] bg-slate-200 dark:bg-slate-700 rounded-md"></div>
                <div className="w-24 h-[46px] bg-slate-200 dark:bg-slate-700 rounded-md"></div>
              </div>
            </div>
          </div>

          {/* Filters Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6 animate-pulse">
            <div className="space-y-6">
              <div>
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20 mb-3"></div>
                <div className="flex flex-wrap gap-1.5">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-7 bg-slate-200 dark:bg-slate-700 rounded-full w-20"></div>
                  ))}
                </div>
              </div>
              <div>
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24 mb-3"></div>
                <div className="flex flex-wrap gap-1.5">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-7 bg-slate-200 dark:bg-slate-700 rounded-full w-24"></div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Cards Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 animate-pulse">
                <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/2 mb-4"></div>
                <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
              </div>
            ))}
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                  <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
                </div>
                <p className="text-slate-600 dark:text-slate-400 font-medium mb-4">{error}</p>
                <button 
                  onClick={loadData} 
                  className="bg-primary hover:bg-primary-dark dark:bg-primary-light dark:hover:bg-primary text-white px-6 py-3 rounded-md transition-colors duration-200 font-medium"
                >
                  {t("admin.retry", "Erneut versuchen")}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {t("admin.categories.title", "Kategorienverwaltung")}
                </h1>
                <p className="text-slate-600 dark:text-slate-400 mt-2">
                  {t("admin.categories.subtitle", "Verwalte alle Wettkampfkategorien und deren Einstellungen.")}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{filteredCategories.length}</div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">Kategorien</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Controls Section */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder={t("admin.categories.search", "Kategorien suchen...")}
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 h-[46px] border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                />
                <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 text-sm"></i>
              </div>

              {/* Actions */}
              <div className="flex gap-2 items-stretch">
                <button 
                  onClick={handleAddCategory}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-md hover:bg-primary-dark dark:hover:bg-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 h-[46px]"
                >
                  <i className="fas fa-plus text-xs"></i>
                  {t("admin.categories.add", "Hinzufügen")}
                </button>
                
                <div className="relative">
                  <button 
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    disabled={isExporting}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-md border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed h-[46px]"
                  >
                    <i className={`fas ${isExporting ? 'fa-spinner fa-spin' : 'fa-download'} text-xs`}></i>
                    {isExporting 
                      ? "Export..." 
                      : "Export"
                    }
                    {!isExporting && <i className="fas fa-chevron-down text-xs"></i>}
                  </button>
                  
                  {showExportMenu && !isExporting && (
                    <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-slate-800 rounded-md shadow-lg border border-slate-200 dark:border-slate-700 z-10">
                      <button
                        onClick={handleExportPDF}
                        className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2"
                      >
                        <i className="fas fa-file-pdf text-red-600 text-xs"></i>
                        <span>Als PDF exportieren</span>
                      </button>
                      <button
                        onClick={handleExportJSON}
                        className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2"
                      >
                        <i className="fas fa-file-code text-blue-600 text-xs"></i>
                        <span>Als JSON exportieren</span>
                      </button>
                    </div>
                  )}
                </div>

                <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-md border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed h-[46px]">
                  <i className={`fas ${isImporting ? 'fa-spinner fa-spin' : 'fa-upload'} text-xs`}></i>
                  {isImporting ? "Import..." : "Import"}
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleImportJSON}
                    disabled={isImporting}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Filter Groups */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
            <div className="space-y-6">
              {/* Type Filter */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Typ</h3>
                <div className="flex flex-wrap gap-1.5">
                  {typeOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleTypeChange(option.value)}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        selectedType === option.value
                          ? 'bg-primary dark:bg-primary-light text-white'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Age Group Filter */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Altersgruppe</h3>
                <div className="flex flex-wrap gap-1.5">
                  {ageGroupOptions.map((option) => {
                    const isSelected = selectedAgeGroup === option.value;
                    
                    if (option.value === "all") {
                      return (
                        <button
                          key={option.value}
                          onClick={() => handleAgeGroupChange(option.value)}
                          className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                            isSelected
                              ? 'bg-primary dark:bg-primary-light text-white'
                              : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    }
                    
                    const colors = getAgeGroupColors(option.value);
                    
                    return (
                      <button
                        key={option.value}
                        onClick={() => handleAgeGroupChange(option.value)}
                        className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                          isSelected
                            ? `${colors.bg} text-white`
                            : `${colors.bgLight} ${colors.text} hover:${colors.bg} hover:text-white`
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t("admin.categories.id", "ID")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t("admin.categories.name", "Name")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t("admin.categories.type", "Typ")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t("admin.categories.ageGroups", "Altersgruppen")}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                      {t("admin.categories.actions", "Aktionen")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                  {filteredCategories.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-500 dark:text-slate-400">
                        {item.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{item.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                          item.type === 'Handform' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
                          item.type === 'Waffenform' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                          item.type === 'Offene Form' ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' :
                          item.type === 'Partnerform' ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' :
                          'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                        }`}>
                          {item.type}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          {sortAgeGroups([...item.ageGroups]).map((ageGroup, index) => {
                            const colors = getAgeGroupColors(ageGroup);
                            // Map bgLight to dark mode equivalents
                            const darkBgMap = {
                              'bg-emerald-100': 'dark:bg-emerald-900/30',
                              'bg-blue-100': 'dark:bg-blue-900/30',
                              'bg-violet-100': 'dark:bg-violet-900/30',
                              'bg-amber-100': 'dark:bg-amber-900/30',
                              'bg-red-100': 'dark:bg-red-900/30',
                              'bg-gray-100': 'dark:bg-gray-700'
                            };
                            // Map text to dark mode equivalents
                            const darkTextMap = {
                              'text-emerald-700': 'dark:text-emerald-300',
                              'text-blue-700': 'dark:text-blue-300',
                              'text-violet-700': 'dark:text-violet-300',
                              'text-amber-700': 'dark:text-amber-300',
                              'text-red-700': 'dark:text-red-300',
                              'text-gray-800': 'dark:text-gray-300'
                            };
                            return (
                              <span
                                key={index}
                                className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${colors.bgLight} ${darkBgMap[colors.bgLight] || 'dark:bg-slate-700'} ${colors.text} ${darkTextMap[colors.text] || 'dark:text-slate-300'}`}
                              >
                                {ageGroup}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-1">
                          <button 
                            onClick={() => handleEditCategory(item)}
                            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                            title={t("admin.categories.edit", "Bearbeiten")}
                          >
                            <i className="fas fa-edit text-sm"></i>
                          </button>
                          <button 
                            onClick={() => handleViewGroups(item)}
                            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                            title={t("admin.categories.viewGroups", "Gruppen anzeigen")}
                          >
                            <i className="fas fa-layer-group text-sm"></i>
                          </button>
                          <button 
                            onClick={() => handleDeleteCategory(item)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title={t("admin.categories.delete", "Löschen")}
                          >
                            <i className="fas fa-trash text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Empty State */}
          {filteredCategories.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-layer-group text-slate-400 dark:text-slate-500 text-xl"></i>
              </div>
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                {t("admin.categories.empty.title", "Keine Kategorien gefunden")}
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
                {searchTerm || selectedType !== "all" || selectedAgeGroup !== "all"
                  ? t("admin.categories.empty.filterMessage", "Versuchen Sie, Ihre Filter zu ändern oder den Suchbegriff anzupassen.")
                  : t("admin.categories.empty.noDataMessage", "Fügen Sie die erste Kategorie hinzu, um zu beginnen.")
                }
              </p>
              {(!searchTerm && selectedType === "all" && selectedAgeGroup === "all") && (
                <button
                  onClick={handleAddCategory}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-md hover:bg-primary-dark dark:hover:bg-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <i className="fas fa-plus text-xs"></i>
                  {t("admin.categories.empty.addButton", "Erste Kategorie hinzufügen")}
                </button>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Modal für Kategorie hinzufügen/bearbeiten */}
      <AdminModalTailwind
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingCategory ? t("admin.categories.editTitle", "Kategorie bearbeiten") : t("admin.categories.addTitle", "Neue Kategorie")}
        subtitle={editingCategory ? t("admin.categories.editSubtitle", "Bearbeite die ausgewählte Kategorie.") : t("admin.categories.addSubtitle", "Erstelle eine neue Wettkampfkategorie.")}
        onSave={() => categoryFormRef.current?.handleSubmit()}
        onCancel={handleCloseModal}
        saveButtonText={editingCategory ? t("admin.update", "Aktualisieren") : t("admin.create", "Erstellen")}
        cancelButtonText={t("admin.cancel", "Abbrechen")}
        isSaving={isSaving}
      >
        <CategoryFormTailwind
          ref={categoryFormRef}
          category={editingCategory}
          onSave={handleSaveCategory}
          onCancel={handleCloseModal}
          isSaving={isSaving}
        />
      </AdminModalTailwind>

      {/* Bestätigungsmodal für Löschen */}
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmDelete}
        title="Kategorie löschen"
        subtitle="Diese Aktion kann nicht rückgängig gemacht werden."
        message={`Möchten Sie die Kategorie "${categoryToDelete?.name}" wirklich löschen?`}
        confirmText="Löschen"
        cancelText="Abbrechen"
        type="danger"
        layout="inline"
      />

      {/* Toast */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`px-4 py-3 rounded-lg border shadow-lg bg-white ${
            toast.type === 'success' ? 'border-green-200' :
            toast.type === 'error' ? 'border-red-200' :
            'border-blue-200'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                toast.type === 'success' ? 'bg-green-100' :
                toast.type === 'error' ? 'bg-red-100' :
                'bg-blue-100'
              }`}>
                <i className={`fas text-xs ${
                  toast.type === 'success' ? 'fa-check text-green-600' :
                  toast.type === 'error' ? 'fa-times text-red-600' :
                  'fa-info text-blue-600'
                }`}></i>
              </div>
              <span className={`text-sm font-medium ${
                toast.type === 'success' ? 'text-green-900' :
                toast.type === 'error' ? 'text-red-900' :
                'text-blue-900'
              }`}>{toast.message}</span>
              <button
                onClick={hideToast}
                className="ml-2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <i className="fas fa-times text-xs"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CategoriesTailwind;